<script setup>
import CreateCustomerUserAction from '@frontJs/components/actions/CreateCustomerUserAction.vue';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import PillsCell from '@js/components/tables/cells/PillsCell.vue';
import StatusCell from '@js/components/tables/cells/StatusCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { archived = undefined, active = undefined } = defineProps({
    archived: {
        type: Boolean,
        required: false,
    },
    active: {
        type: Boolean,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    globalData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'tenant.name',
            header: () => 'Client',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.tenant.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'firstname',
            header: () => 'Prénom',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.firstname,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'lastname',
            header: () => 'Nom',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.lastname,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'email',
            header: () => 'Email',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.email,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'is_active',
            header: () => 'Est actif',
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.isActive,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'roles',
            header: () => 'Rôles',
            cell: (props) =>
                h(PillsCell, {
                    pillValues: props.row.original.roles.map((role) => {
                        return {
                            value: role.name,
                            type: 'secondary',
                        };
                    }),
                }),
            enableSorting: false,
        }),
        columnHelper.display({
            id: 'actions',
            header: 'Actions',
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title: 'Modifier',
                                href: route('front.updateCustomerUser', {
                                    tenant: props.row.original.tenant.id,
                                    tenantUserId: props.row.original.id,
                                }),
                                icon: PencilSimpleIcon,
                            }),
                    },
                ),
            width: 0,
        }),
    ],
    url: route('front.postCustomerUsers'),
    data: {
        archived,
        active,
    },
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    >
        <template #actions>
            <CreateCustomerUserAction :tenants="globalData.tenants">
                <template #trigger>
                    <ActionButton
                        size="small"
                        :left-icon="PlusIcon"
                        type="primary"
                    >
                        Ajouter un utilisateur client
                    </ActionButton>
                </template>
            </CreateCustomerUserAction>
        </template>
    </DataTableLayout>
</template>
